import React, { useState, useEffect, useRef } from 'react';
import { Collection } from '../../types/collections';
import { X, Calendar } from 'react-feather';
import { partnersData } from '../../data/partnersData.ts';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import '../Layout/DatePicker.css';

interface NewCollectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (collectionData: Omit<Collection, 'id' | 'status' | 'createdAt' | 'updatedAt'>) => void;
}

// Define product options
const productOptions = [
  'BNPL', 
  'Tabby Card', 
  'Capital', 
  'Prepaid Ads', 
  'Postpaid Ads', 
  'Acquiring'
];

const NewCollectionModal: React.FC<NewCollectionModalProps> = ({ isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    partnerId: '', // This will now store the partner *name*
    partnerName: '',
    product: '', // NEW: Add product state
    country: '', // NEW: Add country state
    amount: '',
    currency: 'AED',
    dueDate: '', // Keep this as string YYYY-MM-DD
    reference: '',
  });
  const modalContentRef = useRef<HTMLDivElement>(null);
  const datePickerRef = useRef<HTMLDivElement>(null); // Ref for date picker popover

  // State for DatePicker component
  const [selectedDueDate, setSelectedDueDate] = useState<Date | null>(null);
  const [isDatePickerVisible, setIsDatePickerVisible] = useState(false);

  // Handle clicks outside the modal
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalContentRef.current && !modalContentRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    // Cleanup listener on component unmount or when modal closes
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Effect to handle clicks outside the date picker popover
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        // Check if the click target is the button that opens the date picker
        const datePickerButton = document.getElementById('due-date-button');
        if (!datePickerButton || !datePickerButton.contains(event.target as Node)) {
          setIsDatePickerVisible(false);
        }
      }
    };

    if (isDatePickerVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDatePickerVisible]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // Update logic for partner selection using name
    if (name === 'partnerId') {
      // Find partner by name from imported data
      const selectedPartner = partnersData.find(partner => partner.name === value);
      setFormData({
        ...formData,
        partnerId: value, // Store the selected name as the ID/value
        partnerName: selectedPartner ? selectedPartner.name : '', // Name is the same as value
        country: selectedPartner ? selectedPartner.location : '', // NEW: Set country based on partner
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  // Handle date change from DatePicker
  const handleDateChange = (date: Date | null) => {
    if (date) {
      setSelectedDueDate(date);
      // Format date to YYYY-MM-DD for the form state
      const formattedDate = date.toISOString().split('T')[0];
      setFormData({
        ...formData,
        dueDate: formattedDate,
      });
      setIsDatePickerVisible(false); // Close picker after selection
    } else {
      // Handle clearing the date
      setSelectedDueDate(null);
      setFormData({
        ...formData,
        dueDate: '',
      });
    }
  };

  // Format date for display in the button
  const formatDateForDisplay = (date: Date | null): string => {
    if (!date) return 'dd/mm/yyyy';
    // Use a simple DD/MM/YYYY format for display consistency with placeholder
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Month is 0-indexed
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const newCollection: Omit<Collection, 'id' | 'status' | 'createdAt' | 'updatedAt'> = {
      partnerId: formData.partnerId,
      partnerName: formData.partnerName,
      product: formData.product, // NEW: Include product
      country: formData.country, // NEW: Include country
      amount: parseFloat(formData.amount),
      currency: formData.currency,
      dueDate: formData.dueDate, // Already in YYYY-MM-DD format
      reference: formData.reference,
    };
    
    onSubmit(newCollection);
    onClose();
  };

  if (!isOpen) return null;

  const labelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: '6px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#374151',
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    fontSize: '14px',
    boxSizing: 'border-box',
    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  };

  const selectStyle: React.CSSProperties = {
    ...inputStyle,
    appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
    backgroundPosition: 'right 0.5rem center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: '1.5em 1.5em',
    paddingRight: '2.5rem',
  };

  const buttonBaseStyle: React.CSSProperties = {
    padding: '10px 16px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    border: '1px solid transparent',
    transition: 'background-color 0.2s ease, border-color 0.2s ease',
  };

  const primaryButtonStyle: React.CSSProperties = {
    ...buttonBaseStyle,
    backgroundColor: '#111827',
    color: 'white',
    border: '1px solid #111827',
  };

  const secondaryButtonStyle: React.CSSProperties = {
    ...buttonBaseStyle,
    backgroundColor: 'white',
    color: '#374151',
    border: '1px solid #d1d5db',
    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(107, 114, 128, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
        padding: '16px',
      }}
    >
      <div
        ref={modalContentRef}
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
          width: '500px',
          maxWidth: '100%',
          maxHeight: '90vh', // Limit modal height
          display: 'flex', // Use flex for layout
          flexDirection: 'column', // Stack header, form, footer vertically
        }}
      >
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 /* Prevent header shrinking */ }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#111827' }}>Create Collection</h2>
            <p style={{ margin: '4px 0 0', fontSize: '14px', color: '#6b7280' }}>Enter details for the new collection request.</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#6b7280' }}>
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '24px' }}>
          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>Partner</label>
            <select
              name="partnerId"
              value={formData.partnerId}
              onChange={handleChange}
              required
              style={selectStyle}
            >
              <option value="">Select a partner</option>
              {partnersData.map((partner) => (
                <option key={partner.name} value={partner.name}>
                  {partner.name}
                </option>
              ))}
            </select>
          </div>
          
          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>Product</label>
            <select
              name="product"
              value={formData.product}
              onChange={handleChange}
              required
              style={selectStyle}
            >
              <option value="">Select a product</option>
              {productOptions.map((product) => (
                <option key={product} value={product}>
                  {product}
                </option>
              ))}
            </select>
          </div>
          
          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>Amount</label>
            <div style={{ display: 'flex' }}>
              <select
                name="currency"
                value={formData.currency}
                onChange={handleChange}
                style={{
                  ...selectStyle,
                  width: 'auto',
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
                  borderRight: 'none',
                }}
              >
                <option value="AED">AED</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
              </select>
              <input type="number" name="amount" value={formData.amount} onChange={handleChange}
                required min="0" step="0.01"
                placeholder="0.00"
                style={{
                  flex: 1,
                  ...inputStyle,
                  borderTopLeftRadius: 0,
                  borderBottomLeftRadius: 0,
                }}
              />
            </div>
          </div>
          
          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>Due Date</label>
            <div style={{ position: 'relative' }}>
              <button
                id="due-date-button" // Add ID for outside click detection
                type="button"
                onClick={() => setIsDatePickerVisible(!isDatePickerVisible)}
                style={{
                  ...inputStyle, // Use input style for appearance
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between', // Push icon to the right
                  textAlign: 'left',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  color: formData.dueDate ? '#111827' : '#9ca3af', // Placeholder color if no date
                }}
              >
                <span>{formatDateForDisplay(selectedDueDate)}</span>
                <Calendar size={16} style={{ color: '#6b7280' }} />
              </button>

              {isDatePickerVisible && (
                <div
                  ref={datePickerRef} // Add ref to the popover container
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 4px)',
                    left: 0,
                    zIndex: 1010, // Ensure it's above modal backdrop
                    // No background/border needed here, DatePicker handles its own styling
                  }}
                >
                  <DatePicker
                    selected={selectedDueDate}
                    onChange={handleDateChange}
                    inline // Render the calendar directly
                    dateFormat="dd/MM/yyyy" // Match display format
                    calendarClassName="custom-datepicker-style" // Apply custom styles
                  />
                </div>
              )}
            </div>
            {/* Hidden input to satisfy 'required' if needed, or handle validation in JS */}
            <input type="hidden" name="dueDate" value={formData.dueDate} required />
          </div>
          
          <div style={{ marginBottom: '24px' }}>
            <label style={labelStyle}>Reference</label>
            <input type="text" name="reference" value={formData.reference} onChange={handleChange}
              placeholder="Collection reference number"
              required
              style={inputStyle}
            />
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '24px', borderTop: '1px solid #e5e7eb', flexShrink: 0 /* Prevent footer shrinking */ }}>
            <button
              type="button"
              onClick={onClose}
              style={secondaryButtonStyle}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={primaryButtonStyle}
            >
              Create Collection
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewCollectionModal; 